/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../lib/@waca/core-client/js/core-client/utils/PerfUtils',
	'./ContentAPISpec',
	'../api/ContentAPI',
	'../features/FeatureLoader',
	'../dashboard/util/PropertyLayoutHelper',
	'../features/content/state/impl/StateImpl',
	'../features/content/internalModels/InternalModels',
	'../features/content/contentFilter/ContentFilter',
	'../features/content/serializer/Serializer',
	'../dashboard/util/DashboardSpecHelper'
], function (_, APIFactory, PerfUtils, ContentAPISpec, ContentAPI, FeatureLoader, PropertyLayoutHelper, State, InternalModels, ContentFilter, Serializer, DashboardSpecHelper) {


	class Content extends ContentAPISpec {

		constructor(options) {
			super();
			this.boardModel = options.boardModel;
			this.layoutModel = options.layoutModel;
			this.profile = options.profile;
			this.creationTransactionToken = options.transactionToken;

			// TODO: revisit this
			// The widget model can be resolved based on the layout model and the boardModel
			// The only time it is passed in here is for the case of the LiveWidgetPreview
			// where we need to create a content API for a widget model that is disconnected
			this.widgetModel = options.widgetModel;
			this.contentMap = {};
			this.container = options.container;
			this.featureLoaderClass = options.featureLoaderClass || FeatureLoader;
			this.featureLoader = options.featureLoader;
			this.canvas = options.canvas;

			this.dashboardFeatures = options.dashboardFeatures;
			this.transaction = this.dashboardFeatures.getFeature('Transaction');
			this.lifeCycleManager = this.dashboardFeatures.getFeature('LifeCycle');
			this.logger = this.dashboardFeatures.getFeature('Logger');
			this.contentFactory = this.dashboardFeatures.getFeature('ContentFactory');
			this.dashboardSettings = this.dashboardFeatures.getFeature('DashboardSettings');
			if (!this.featureLoader) {
				this.createFeatureLoader();
			}
			this._updateContentModelFeatures();

			const model = this._getModel();
			model.on('add:item', this.onAddItem, this);
			model.on('remove:item', this.onRemoveItem, this);
			model.on('change:parent', this.onChangeParent, this);
			model.on('pre:remove:item', this.preRemoveItem, this);

			const featureChecker = this.dashboardFeatures.getFeature('API').getGlassCoreSvc('.FeatureChecker');
			this._isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');

			this._contentFeatureCollection =  options.contentFeatureCollection || [];
			this.createChildren();

		}

		/**
		 * In this method we are directly modifing the content model
		 * (ContentModel.js) instance's "features" property object.
		 * Eventually ContentModel will go away at which point the following
		 * will required to be changed to not modify another object's instance
		 * @private
		 */
		_updateContentModelFeatures() {
			const getLoadedFeatures = this.featureLoader.getLoadedFeatures.bind(this.featureLoader);
			const { features: contentModelFeatures } = this._getModel().getContentModel();
			const IGNORE_CONTENT_FEATURES = ['toJSON', 'Serializer'];
			contentModelFeatures.toJSON = () => {
				const loadedFeatures = getLoadedFeatures();
				const featureNames = Object.keys(loadedFeatures)
					.concat(Object.keys(contentModelFeatures))
					.filter(item => IGNORE_CONTENT_FEATURES.indexOf(item) === -1);

				const features = {};
				featureNames.forEach(featureName => {
					let feature = null;
					if (loadedFeatures[featureName] &&
						typeof loadedFeatures[featureName].toJSON === 'function'
					) {
						feature = loadedFeatures[featureName].toJSON();
					} else if (contentModelFeatures[featureName]) {
						feature = contentModelFeatures[featureName];
					} else {
						this.logger.info(`Data for feature "${featureName}" is not persisted`);
					}

					if (feature && Object.keys(feature).length > 0) {
						features[featureName] = feature;
					}
				});

				if (features.hasOwnProperty('Models.internal')) {
					features['Models_internal'] = features['Models.internal'];
					delete features['Models.internal'];
				}

				return features;
			};
		}

		onAddItem(payload) {
			const newModelId = payload.value.parameter.model.id;
			//currently, in getContentInstance(), we will auto-generate the missing contentInstance.
			this.getContentInstance(newModelId, false, payload.data ? payload.data.transactionToken: null);
		}

		preRemoveItem(payload) {
			const childId = payload.value.parameter;
			const contentImpl = this.getContentInstance(childId);
			if (contentImpl) {
				const content = contentImpl.getAPI();
				const contentViewDom = content.getFeature('ContentViewDOM');
				const contentNode = contentViewDom && contentViewDom.getNode();
				if (contentNode) {
					const dashboardAPI = this.dashboardFeatures.getFeature('API');
					const controller = dashboardAPI.getFeature('InteractionController.internal');
					controller.selectionHandler.deselectNode(contentNode);
				}
			}
		}

		onRemoveItem(payload) {
			const childId = payload.value.parameter;
			const contentImpl = this.getContentInstance(childId);
			if (contentImpl) {
				contentImpl.destroy();
				delete this.contentMap[childId];
			}
		}

		onChangeParent(payload) {
			const newParentId = payload.value.parameter.parentId;
			if (this.getContainer().getId() !== newParentId) {
				const newContainerInst = this.canvas.getRootContentInstance().getContentInstance(newParentId);
				this.moveToContainer(newContainerInst);
			}
		}

		/**
		 * create the feature loader, and register core features before initialization,
		 * the core features includes:
		 * - state
		 * - Models.internal
		 * - ContentFilter
		 * - Serializer
		 */
		createFeatureLoader() {
			const type = this.getType();
			const types = [type];

			if (type !== this._getModel().type) {
				types.push(this._getModel().type);
			}

			const currProfile = this.dashboardFeatures && this.dashboardFeatures.getCurrentProfile && this.dashboardFeatures.getCurrentProfile();
			const enableProfileFeatureLoading = (currProfile !== undefined);

			this.featureLoader = new this.featureLoaderClass({
				featureParams: {
					// todo - we should not pass the dashboard API to the features like this
					dashboardAPI: this.dashboardFeatures.getFeature('API'),
					content: this.getAPI(),
					featureSpecs: this._getModel().getContentModel().features,
					transactionToken: this.creationTransactionToken
				},
				profileList: currProfile,
				enableProfileFeatureLoading: enableProfileFeatureLoading,
				externalFeatures: this.dashboardFeatures,
				lifeCycleManager: this.lifeCycleManager,
				types: types,
				parentAPI: this
			});

			// State feature is a core feature that requires to be available prior to initialize
			this.featureLoader.registerFeature('state', new State({
				logger: this.logger
			}), {
				'containerId': 'com.ibm.bi.dashboard.content-features',
				'name': 'state',
				'types': ['widget'],
				'profiles': ['core']
			});

			this.featureLoader.registerFeature('Models.internal', new InternalModels({
				widgetModel: this.widgetModel,
				layoutModel: this.layoutModel
			}), {
				'containerId': 'com.ibm.bi.dashboard.content-features',
				'name': 'InternalModels',
				'profiles': ['core']
			});

			// Content filter is required to be available before initialization
			this.featureLoader.registerFeature('ContentFilter', new ContentFilter({
				content: this,
				logger: this.logger
			}), {
				'containerId': 'com.ibm.bi.dashboard.content-features',
				'name': 'ContentFilter',
				'profiles': ['core']
			});

			// Content serializer is required to be available before initialization,
			// as the toJSON can be called in the middle of creation of a content
			this.featureLoader.registerFeature('Serializer', new Serializer({
				layoutModel : this.layoutModel,
				content: this,
				logger: this.logger
			}), {
				'containerId': 'com.ibm.bi.dashboard.content-features.Serializer',
				'name': 'Serializer',
				'profiles': ['core']
			});

			// to register inline content features in test-api
			this.featureLoader.registerFeature('FeatureRegistry.internal', {
				getAPI: () =>  this.featureLoader.getAPI()
			}, {
				'containerId': 'com.ibm.bi.dashboard.content-features',
				'name': 'FeatureRegistry.internal',
				'profiles': ['core']
			});
			this._createdFeatureLoader = true;
		}

		createChildren(){
			const items = this._getModelItems();
			if (items) {
				items.forEach(element => {
					this.getContentInstance(element.id, true, this.creationTransactionToken);
				});
			}
		}

		setContentFeatures(contentFeatureCollection) {
			this._contentFeatureCollection = contentFeatureCollection;
			for (let id in this.contentMap){
				this.contentMap[id].setContentFeatures(contentFeatureCollection);
			}
		}

		initialize() {
			if (!this._initializePromise) {
				const timeMarker = 'content ' + this.api.getId() + ' initialize';
				PerfUtils.createPerformanceMark({
					'component': 'dashboard',
					'name': timeMarker,
					'state': 'start'
				});
				const promises = [];
				promises.push(this.featureLoader.loadFeaturesFromArray(this._contentFeatureCollection));
				const items = this._getModelItems();
				if (items) {

					// if it is a tab content, we only initilize the first tab
					// the other tabs will be initialized automatically when the layout view is created and registers the dom feature
					// the initialize of the content is triggered here when the layout is created
					// https://github.ibm.com/BusinessAnalytics/dashboard-core/blob/master/js/dashboard/layout/views/LayoutBaseView.js#L327
					// TODO - we need a better content feature delay loading life cycle for the tabs.
					// This is on enabled when the API is created from the dashboard BaseBoardView
					if (this.dashboardSettings.get('contentFeatureDelayLoadTabs') &&  this.getType() === 'tab'){
						const childContent = this.getContentInstance(items[0].id, true, this.creationTransactionToken);
						if (childContent) {
							promises.push(childContent.initialize());
						}
					} else {
						// Walk the children and initialize the child content
						items.forEach(element => {
							const childContent = this.getContentInstance(element.id, true, this.creationTransactionToken);
							if (childContent) {
								promises.push(childContent.initialize());
							}
						});
					}
				}
				this._initializePromise = Promise.all(promises).then(() => {
					const state = this.getFeature('state.internal');
					state && state.setStatus(state.STATUS.INITIALIZED);
					PerfUtils.createPerformanceMark({
						'component': 'dashboard',
						'name': timeMarker,
						'state': 'end'
					});
				});
			}
			return this._initializePromise
				.then(() => this.getAPI());
		}

		registerFeatures(features){
			return this.featureLoader.loadFeaturesFromArray(features);
		}

		/**
		 * A function to manually register a feature. Could be used by the dashboard framework to register low level features.
		 * This method should not be exposed through the content API interface
		 *  @param {String} name
		 *  @param {Object} instance
		 *  @param {Object} spec
		 */
		registerFeature(name, instance, spec, skipInitialization) {
			return this.initialize().then(() => {
				return this.featureLoader.registerFeature(name, instance, spec, skipInitialization);
			});
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [ContentAPI]);
			}
			return this.api;
		}

		destroyContentMap() {
			Object.keys(this.contentMap).forEach((childID) => {
				const childContent = this.contentMap[childID];
				childContent && childContent.destroy && childContent.destroy();
			});
		}

		destroy() {
			const model = this._getModel();
			model.off('add:item', this.onAddItem, this);
			model.off('remove:item', this.onRemoveItem, this);
			model.off('change:parent', this.onChangeParent, this);

			this.destroyContentMap();
			if (this._createdFeatureLoader) {
				this.featureLoader.destroy();
			}
			this.featureLoader = null;
			this.dashboardFeatures = null;
			this.boardModel = null;
			this.layoutModel = null;
			this.widgetModel = null;
			this.contentMap = null;
			this.canvas = null;
			this.api = null;
		}

		getId() {
			return this._getModel().getId();
		}

		_getWidgetModel(){
			if (!this.widgetModel){
				this.widgetModel = this.boardModel.getWidgetModel(this.getId());
			}
			return this.widgetModel;
		}

		getType() {
			let type = this.layoutModel.type;

			const widgetModel = this._getWidgetModel();
			if (widgetModel) {
				type += '.' + widgetModel.type;
			}

			return type;
		}

		addContent(options, transactionToken) {
			if (_.isNumber(options.position)) {
				const beforeContent = this.getChildren()[options.position];
				options.insertBefore = beforeContent && beforeContent.getId();
			}

			if (options.content) {
				return this._getContentSpec(options.content).then((spec) => {
					if (spec) {
						options.type = options.content.getType();
						spec.layout = spec.layout[0];
						options.spec = spec;
					}
					return this._addModel(options, transactionToken);
				});
			}
			return this._addModel(options, transactionToken);
		}

		_addModel(options, transactionToken) {
			//Todo: temporary until all uses are updated to use the properties
			options.properties = Object.assign(options.properties || {}, options.layout);
			delete options.layout;

			return this.contentFactory.addModel(options, {
				boardModel: this.boardModel,
				canvas: this.canvas,
				canvasContent: this
			}, transactionToken).then((modelId) => {
				return this.getContentInstance(modelId).initialize();
			}).then((contentApi) => {
				const properties = options.properties || {};
				Object.keys(properties).forEach((propertyName) => {
					contentApi.setPropertyValue(propertyName, properties[propertyName], transactionToken);
				});
				return contentApi;
			});
		}

		_getContentSpec(content) {
			// This is a unique case for addContent where the added content may be coming from a different dashboard.
			// Conscious choice not to expose a content api to get the dashboard api since:
			// 1. This can go away when the Serializer is able to return the currently missing dashboard level information relating to a content. See DashboardSpecHelper for specifics.
			// 2. Worried it may be abused, especially in content features.
			const dashboard = content.getFeature('Models.internal').getLayoutModel().boardModel.dashboardApi;
			const layoutController = dashboard.getFeature('LegacyViewControllers').getLayoutController();
			return layoutController.getInteractionController().then((controller) => {
				const specHelper = new DashboardSpecHelper(controller);
				let contentSpec = specHelper.getContentsToJSONSpec(content).spec;
				return specHelper.validateDashboardSpec(contentSpec);
			});
		}

		removeChildContent(id, transactionToken){
			// Content can only delete its children.. make sure the content belongs to this container before deleting it
			const content = this.getContentInstance(id);
			if (content) {
				this.boardModel.removeLayouts(id, 'api', this._convertOptions(transactionToken));
			}
		}

		removeContentReference(content) {
			delete this.contentMap[content.getId()];
		}

		addContentReference(content) {
			this.contentMap[content.getId()] = content;
		}

		moveToContainer(newContainerInst) {
			this.container.removeContentReference(this);
			this.container = newContainerInst;
			newContainerInst.addContentReference(this);
		}

		moveContent(containerId, contentIdList, transactionToken, insertBeforeMap) {
			const containerInst = containerId === this.getId() ? this: this.getContentInstance(containerId);
			if(containerInst) {
				var moveOptions = {
					updateArray:[]
				};

				const contentInstList = contentIdList.map((id) => this.canvas.getContentInstance(id));
				contentInstList.forEach((content)=>{
					if (content) {
						const id = content.getId();
						let contentEntry = {
							id: id,
							parentId: containerInst.getId()
						};
						if (insertBeforeMap && insertBeforeMap[id]) {
							contentEntry.insertBefore = insertBeforeMap[id];
						}
						moveOptions.updateArray.push(contentEntry);
						content.moveToContainer(containerInst);
					}
				});

				if (moveOptions.updateArray.length > 0) {
					this.layoutModel.getTopLayoutModel().updateModel(moveOptions, null, this._convertOptions(transactionToken));
				}

				return contentInstList.map(instance => instance.getAPI());
			} else {
				this.logger.error(`cannot find container ${containerId}`);
			}
		}

		removeContent(id, transactionToken) {
			this.removeChildContent(id, transactionToken);
		}

		updateCurrentProfile( params ) {
			let promises = [];
			promises.push( this.featureLoader.updateCurrentProfile(params) );

			Object.keys(this.contentMap).forEach((childID) => {
				const childContent = this.contentMap[childID];
				promises.push( childContent.updateCurrentProfile(params) );
			});
			return Promise.all(promises);
		}

		getCurrentProfile() {
			return this.featureLoader.getCurrentProfile();
		}

		getLoadedFeatureProfileInfo() {
			let aggregatedInfo = {};
			this._getLoadedFeatureProfileInfo(aggregatedInfo);
			return aggregatedInfo;
		}

		_getLoadedFeatureProfileInfo(aggregatedInfo) {
			const info = this.featureLoader.getLoadedFeatureProfileInfo();
			aggregatedInfo[this.getId()] = info;
			Object.keys(this.contentMap).forEach((childID) => {
				const childContent = this.contentMap[childID];
				childContent._getLoadedFeatureProfileInfo(aggregatedInfo);
			});
		}

		getContentInstance(id, doNotInitialize, transactionToken) {
			let content = this.contentMap[id];
			const items = this._getModelItems();
			if (!content && items) {
				const matchedModel = items.find((model) => {
					return model.id === id;
				});

				if (matchedModel) {

					// TODO -- we should not get here but it is ok for now
					// All direct children content API should be available and initialized
					//  Today, we will get here, if a widget was added in authoring, and someone is trying to get the content API
					// The right way is to go through the content API to add content which will give the parent content to properly create and initalize, or use model event to monitor the add/remove of content
					content = this.contentFactory.createContent({
						dashboardFeatures: this.dashboardFeatures,
						boardModel: this.boardModel,
						layoutModel: matchedModel,
						featureLoaderClass: this.featureLoaderClass,
						canvas: this.canvas,
						container: this,
						profile: this.profile,
						transactionToken: transactionToken,
						type: matchedModel.type
					});

					content.setContentFeatures(this._contentFeatureCollection);
					APIFactory.setParentChildRelation(this.canvas, content);

					if (!doNotInitialize) {
						content.initialize();
					}

					this.contentMap[id] = content;
				} else {
					// Ask the children to find the instance;
					for (let i = 0; i < items.length && !content; i++) {
						const contentInstance = this.getContentInstance(items[i].id, false, transactionToken);
						content = contentInstance.getContentInstance(id, doNotInitialize, transactionToken);
					}
				}
			}
			return content;
		}

		getContentPosition(id) {
			let position  = -1;
			const items = this._getModelItems();
			if (items && items.length) {
				position = items.findIndex(model => {
					return model.id === id;
				});
				if (position  === -1) {
					// Find the instance from children and return its position
					for (let i = 0; i < items.length && position === -1; i++) {
						const contentInstance = this.getContentInstance(items[i].id);
						position = contentInstance.getContentPosition(id);
					}
				}
			}
			return position;
		}

		getContent(id) {
			let content = this.getContentInstance(id);
			return content ? content.getAPI() : null;
		}

		getContainer() {
			const containerInst = this.getContainerInstance();
			return containerInst && containerInst.getAPI();
		}

		getContainerInstance() {
			return this.container;
		}

		getChildren() {
			const items = this._getModelItems() || [];
			return items.map(item => this.getContent(item.id));
		}

		findContent(selector = {}) {
			if (_.isEmpty(selector)){
				selector.type = 'widget';
			}
			const contents = [];
			const items = this._getModelItems();
			if (items) {
				items.forEach((item) => {
					const content = this.getContent(item.id);
					const contentFilter = content.getFeature('ContentFilter');
					if (contentFilter.match({ selector, layout: item })) {
						contents.push(content);
					}
					contents.push.apply(contents, content.findContent(selector));
				});
			}
			return contents;
		}

		_convertOptions(transactionToken) {
			const oldOptions = {
				skipUndoRedo: true
			};
			if (transactionToken && transactionToken.transactionId) {
				oldOptions.undoRedoTransactionId = transactionToken.transactionId;
				oldOptions.transactionToken = transactionToken;
			}
			return oldOptions;
		}

		getPropertyLayoutList() {
			const properties = this.getFeature('Properties');
			const layoutList = properties.getPropertyLayoutList();
			return PropertyLayoutHelper.getExpandedLayoutList(layoutList, this.getPropertyList(), this.profile, this._isPropertiesV2Enabled);
		}

		getPropertyNameList() {
			const properties = this.getFeature('Properties');
			return properties.getPropertyList().map((prop) => prop.id);
		}

		// TODO: the third param options should always be only transactionToken
		// but currently in several places we call setPropertyValue with
		// the `options` being transactionToken + some other options, we should avoid pollute transactionToken
		setPropertyValue(name, value, options) {
			const property = this.getPropertyList().find((prop) => {
				return prop.id === name;
			});
			if (property) {
				return property.setPropertyValue(value, options);
			} else {
				return this._getModel().getContentModel().setPropertyValue(name, value, options);
			}
		}

		getPropertyValue(name) {
			const property = this.getPropertyList().find((property) => {
				return property.id === name;
			});
			if (property) {
				return property.getPropertyValue();
			} else {
				return this._getModel().getContentModel().getPropertyValue(name);
			}
		}

		__getPropertyValueFunc (getterFunction, property) {
			let propValue;
			if (getterFunction){
				propValue = getterFunction();
			}else {
				propValue =  this._getModel().getContentModel().getPropertyValue(property.id);
			}
			// Use == instead of === to check for both null and undefined
			if (propValue == null && property.defaultValue != null) {
				propValue = property.defaultValue;
			}

			return propValue;
		}

		getPropertyList() {
			const propertiesFeature = this.getFeature('Properties');
			if (!propertiesFeature) {
				return [];
			}
			const properties = propertiesFeature.getPropertyList();


			// Default any missing get/set methods
			// This is the case for any new property added through the property provider
			for (let i = 0; i < properties.length; i++) {
				const property = properties[i];

				if (!property.isGetterSet) {
					// Make sure we don't set the getPropertyValue over and over against
					// The property list get s called every time..
					property.isGetterSet = true;
					const originalPropertyGetter = property.getPropertyValue && property.getPropertyValue.bind(property);
					property.getPropertyValue = this.__getPropertyValueFunc.bind(this, originalPropertyGetter, property);
				}
				if (!property.setPropertyValue) {
					// If a setPropertyValue is not defined, we assume it is a new property added by a feature provider
					// in this case will tag it to support api events
					property.setPropertyValue = (value, options) => {
						this._getModel().getContentModel().setPropertyValue(property.id, value, options);
					};
				}
			}

			return properties;
		}

		getFeature(id) {
			if (this.featureLoader) {
				return this.featureLoader.getFeature(id);
			}
		}

		setFeatureEnabled(id, isEnabled) {
			if (this.featureLoader) {
				this.featureLoader.setFeatureEnabled(id, isEnabled);
			}
		}

		_getModel() {
			return this.layoutModel;
		}

		_getModelItems() {
			return this.layoutModel.items;
		}

	}

	return Content;
});
